import React, { useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';

const BACKGROUND_COLORS = [
  { name: 'Тёмный', value: '#0f172a' },
  { name: 'Чёрный', value: '#000000' },
  { name: 'Белый', value: '#ffffff' },
  { name: 'Серый', value: '#374151' },
  { name: 'Синий', value: '#1e3a5f' },
  { name: 'Пурпурный', value: '#3b1d4a' },
  { name: 'Изумруд', value: '#134e4a' },
  { name: 'Розовый', value: '#4a1d3b' },
  { name: 'Бежевый', value: '#d4c4a8' },
  { name: 'Коричневый', value: '#5c4033' },
  { name: 'Красный', value: '#7f1d1d' },
  { name: 'Оранжевый', value: '#9a3412' },
];

const GRADIENTS = [
  { name: 'Закат', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { name: 'Океан', value: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)' },
  { name: 'Лес', value: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)' },
  { name: 'Огонь', value: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)' },
  { name: 'Ночь', value: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
  { name: 'Пастель', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
];

interface BackgroundPanelProps {
  backgroundColor: string;
  backgroundImage: string | null;
  onBackgroundColorChange: (value: string) => void;
  onBackgroundImageChange: (image: string | null) => void;
}

const BackgroundPanel: React.FC<BackgroundPanelProps> = ({
  backgroundColor,
  backgroundImage,
  onBackgroundColorChange,
  onBackgroundImageChange,
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onBackgroundImageChange(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-4">
      <h2 className="panel-title font-semibold text-foreground">{t.collageBackground}</h2>

      {/* Custom background image */}
      <div className="space-y-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        
        {backgroundImage ? (
          <div className="relative rounded-lg overflow-hidden">
            <img src={backgroundImage} alt="Background" className="w-full h-20 object-cover" />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-1 right-1 h-6 w-6"
              onClick={() => onBackgroundImageChange(null)}
              aria-label="Remove background image"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-2" />
            {t.uploadBackground}
          </Button>
        )}
      </div>

      {/* Colors */}
      <div className="space-y-2">
        <Label className="panel-label text-muted-foreground">{t.colors}</Label>
        <div className="grid grid-cols-6 gap-1.5">
          {BACKGROUND_COLORS.map((color) => (
            <button
              key={color.value}
              onClick={() => {
                onBackgroundImageChange(null);
                onBackgroundColorChange(color.value);
              }}
              aria-label={`Background color ${color.name}`}
              className={`aspect-square rounded-md border-2 transition-all duration-200 hover:scale-105 ${
                backgroundColor === color.value && !backgroundImage
                  ? 'border-primary shadow-[0_0_8px_hsl(190_100%_50%/0.4)]'
                  : 'border-transparent hover:border-primary/50'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      {/* Gradients */}
      <div className="space-y-2">
        <Label className="panel-label text-muted-foreground">{t.gradients}</Label>
        <div className="grid grid-cols-3 gap-1.5">
          {GRADIENTS.map((gradient) => (
            <button
              key={gradient.name}
              onClick={() => {
                onBackgroundImageChange(null);
                onBackgroundColorChange(gradient.value);
              }}
              aria-label={`Gradient ${gradient.name}`}
              className={`aspect-video rounded-md border-2 transition-all duration-200 hover:scale-105 ${
                backgroundColor === gradient.value && !backgroundImage
                  ? 'border-primary shadow-[0_0_8px_hsl(190_100%_50%/0.4)]'
                  : 'border-transparent hover:border-primary/50'
              }`}
              style={{ background: gradient.value }}
              title={gradient.name}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BackgroundPanel;